import React, { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import {
  Search, Filter, Eye, CheckCircle, XCircle, Clock, Upload,
  Trash2, FileText, Image, User, Car, Phone, Mail, MapPin,
  ChevronDown, ChevronUp, Download, RefreshCw, Shield, AlertTriangle,
  X
} from "lucide-react";

const safeFetch = (url, options = {}) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(options.method || "GET", url, true);
    const headers = options.headers || {};
    Object.entries(headers).forEach(([key, value]) => {
      if (value) xhr.setRequestHeader(key, value);
    });
    xhr.onload = function () {
      let data;
      try { data = xhr.responseText ? JSON.parse(xhr.responseText) : null; }
      catch { data = xhr.responseText; }
      resolve({
        ok: xhr.status >= 200 && xhr.status < 300,
        status: xhr.status,
        json: () => Promise.resolve(data),
        text: () => Promise.resolve(xhr.responseText),
      });
    };
    xhr.onerror = () => reject(new Error("Network error"));
    if (options.body) {
      if (options.body instanceof FormData) {
        xhr.send(options.body);
      } else {
        xhr.send(typeof options.body === "string" ? options.body : JSON.stringify(options.body));
      }
    } else {
      xhr.send();
    }
  });
};

const statusBadge = (status) => {
  const map = {
    approved: { cls: "bg-emerald-100 text-emerald-700 border-emerald-200", icon: CheckCircle },
    verified: { cls: "bg-emerald-100 text-emerald-700 border-emerald-200", icon: CheckCircle },
    rejected: { cls: "bg-red-100 text-red-700 border-red-200", icon: XCircle },
    pending: { cls: "bg-amber-100 text-amber-700 border-amber-200", icon: Clock },
  };
  const m = map[status] || map.pending;
  const Icon = m.icon;
  return (
    <Badge className={`${m.cls} border rounded-full text-xs flex items-center gap-1 w-fit`}>
      <Icon className="w-3 h-3" /> {status || "pending"}
    </Badge>
  );
};

export default function DocumentManagement({ isSuperAdmin = false }) {
  const { token } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [docTypeFilter, setDocTypeFilter] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [expandedUser, setExpandedUser] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [verifyDialog, setVerifyDialog] = useState({ open: false, userId: "", docType: "", currentStatus: "" });
  const [verifyNote, setVerifyNote] = useState("");
  const [uploadDialog, setUploadDialog] = useState({ open: false, userId: "", fieldKey: "" });

  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
  const fetchOpts = { headers };

  const fetchDocuments = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      if (statusFilter) params.set("status", statusFilter);
      if (docTypeFilter) params.set("doc_type", docTypeFilter);
      if (roleFilter) params.set("role_filter", roleFilter);
      const res = await safeFetch(`${API}/admin/documents/all?${params.toString()}`, fetchOpts);
      if (res.ok) setDocuments(await res.json());
    } catch (e) { console.error(e); toast.error("Failed to load documents"); }
    setLoading(false);
  }, [search, statusFilter, docTypeFilter, roleFilter, token]);

  useEffect(() => { fetchDocuments(); }, [fetchDocuments]);

  const fetchDriverDetail = async (userId) => {
    if (expandedUser === userId) { setExpandedUser(null); setDetailData(null); return; }
    try {
      const res = await safeFetch(`${API}/admin/documents/driver/${userId}`, fetchOpts);
      if (res.ok) {
        setDetailData(await res.json());
        setExpandedUser(userId);
      }
    } catch (e) { console.error(e); toast.error("Failed to load details"); }
  };

  const fetchCustomerDetail = async (userId) => {
    if (expandedUser === userId) { setExpandedUser(null); setDetailData(null); return; }
    try {
      const res = await safeFetch(`${API}/admin/documents/customer/${userId}`, fetchOpts);
      if (res.ok) {
        setDetailData(await res.json());
        setExpandedUser(userId);
      }
    } catch (e) { console.error(e); toast.error("Failed to load details"); }
  };

  const handleVerify = async (status) => {
    try {
      const res = await safeFetch(`${API}/admin/documents/driver/${verifyDialog.userId}/verify`, {
        method: "PUT", ...fetchOpts,
        body: JSON.stringify({ document_type: verifyDialog.docType, status, note: verifyNote }),
      });
      if (res.ok) {
        toast.success(`Document ${status}`);
        setVerifyDialog({ open: false, userId: "", docType: "", currentStatus: "" });
        setVerifyNote("");
        fetchDocuments();
        if (expandedUser === verifyDialog.userId) fetchDriverDetail(verifyDialog.userId).then(() => fetchDriverDetail(verifyDialog.userId));
      }
    } catch (e) { console.error(e); toast.error("Error verifying document"); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      const uploadRes = await safeFetch(`${API}/admin/documents/upload/${uploadDialog.userId}`, {
        method: "POST", headers: { Authorization: `Bearer ${token}` }, body: formData,
      });
      if (uploadRes.ok) {
        const data = await uploadRes.json();
        // Update the document URL
        await safeFetch(`${API}/superadmin/documents/driver/${uploadDialog.userId}/update`, {
          method: "PUT", ...fetchOpts,
          body: JSON.stringify({ field_key: uploadDialog.fieldKey, document_url: data.url }),
        });
        toast.success("Document uploaded");
        setUploadDialog({ open: false, userId: "", fieldKey: "" });
        fetchDocuments();
        if (expandedUser) { setExpandedUser(null); setDetailData(null); }
      }
    } catch (e) { console.error(e); toast.error("Upload failed"); }
  };

  const handleDelete = async (userId, docType) => {
    if (!confirm("Delete this document?")) return;
    try {
      const res = await safeFetch(`${API}/superadmin/documents/driver/${userId}/${docType}`, {
        method: "DELETE", ...fetchOpts,
      });
      if (res.ok) {
        toast.success("Document deleted");
        fetchDocuments();
        if (expandedUser) { setExpandedUser(null); setDetailData(null); }
      }
    } catch (e) { console.error(e); toast.error("Delete failed"); }
  };

  const getDocUrl = (url) => {
    if (!url) return null;
    if (url.startsWith("http")) return url;
    return `${API.replace("/api", "")}${url.startsWith("/") ? url : "/" + url}`;
  };

  const isPdf = (url) => url && url.toLowerCase().endsWith(".pdf");

  const pendingCount = documents.filter(d => d.approval_status === "pending").length;
  const approvedCount = documents.filter(d => d.approval_status === "approved").length;

  return (
    <div data-testid="document-management" className="space-y-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Document Management</h2>
          <p className="text-sm text-slate-500 mt-1">View and manage all uploaded documents</p>
        </div>
        <div className="flex gap-2">
          <Badge className="bg-amber-100 text-amber-700 rounded-full px-3 py-1">{pendingCount} Pending</Badge>
          <Badge className="bg-emerald-100 text-emerald-700 rounded-full px-3 py-1">{approvedCount} Approved</Badge>
          <Button data-testid="doc-refresh-btn" variant="outline" size="sm" className="rounded-full" onClick={fetchDocuments}>
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      {/* Search & Filters */}
      <Card className="rounded-2xl border-0 shadow-sm">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                data-testid="doc-search"
                placeholder="Search by name, phone, or email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 rounded-xl"
              />
            </div>
            <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
              <SelectTrigger data-testid="doc-status-filter" className="w-full sm:w-40 rounded-xl">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
            <Select value={docTypeFilter} onValueChange={(v) => setDocTypeFilter(v === "all" ? "" : v)}>
              <SelectTrigger data-testid="doc-type-filter" className="w-full sm:w-44 rounded-xl">
                <SelectValue placeholder="Document Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="aadhaar">Aadhaar</SelectItem>
                <SelectItem value="license">License</SelectItem>
                <SelectItem value="rc">Vehicle RC</SelectItem>
                <SelectItem value="insurance">Insurance</SelectItem>
                <SelectItem value="profile_photo">Profile Photo</SelectItem>
              </SelectContent>
            </Select>
            <Select value={roleFilter} onValueChange={(v) => setRoleFilter(v === "all" ? "" : v)}>
              <SelectTrigger data-testid="doc-role-filter" className="w-full sm:w-36 rounded-xl">
                <SelectValue placeholder="Role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Roles</SelectItem>
                <SelectItem value="driver">Drivers</SelectItem>
                <SelectItem value="customer">Customers</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Documents Table */}
      <Card className="rounded-2xl border-0 shadow-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="text-slate-600 text-xs uppercase font-semibold">User</TableHead>
              <TableHead className="text-slate-600 text-xs uppercase font-semibold">Role</TableHead>
              <TableHead className="text-slate-600 text-xs uppercase font-semibold">Contact</TableHead>
              <TableHead className="text-slate-600 text-xs uppercase font-semibold">Documents</TableHead>
              <TableHead className="text-slate-600 text-xs uppercase font-semibold">Status</TableHead>
              <TableHead className="text-slate-600 text-xs uppercase font-semibold">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {documents.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center py-12 text-slate-400">No documents found</TableCell></TableRow>
            )}
            {documents.map((d) => (
              <React.Fragment key={d.user_id}>
                <TableRow data-testid={`doc-row-${d.user_id}`} className="hover:bg-slate-50 cursor-pointer" onClick={() => d.role === "driver" ? fetchDriverDetail(d.user_id) : fetchCustomerDetail(d.user_id)}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-slate-200 flex items-center justify-center overflow-hidden">
                        {d.profile_photo ? (
                          <img src={getDocUrl(d.profile_photo)} alt="" className="w-full h-full object-cover" />
                        ) : d.role === "driver" ? (
                          <Car className="w-4 h-4 text-slate-500" />
                        ) : (
                          <User className="w-4 h-4 text-slate-500" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-slate-900 text-sm">{d.name || "N/A"}</p>
                        {d.vehicle_number && <p className="text-xs text-slate-400 font-mono">{d.vehicle_number}</p>}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge className={`rounded-full text-xs ${d.role === "driver" ? "bg-blue-100 text-blue-700" : "bg-purple-100 text-purple-700"}`}>
                      {d.role}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <p className="text-sm text-slate-700">{d.phone}</p>
                    <p className="text-xs text-slate-400">{d.email}</p>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-slate-700">{d.documents?.length || 0} docs</span>
                  </TableCell>
                  <TableCell>{statusBadge(d.approval_status)}</TableCell>
                  <TableCell>
                    <Button variant="ghost" size="sm" className="h-8 gap-1 text-xs">
                      {expandedUser === d.user_id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      View
                    </Button>
                  </TableCell>
                </TableRow>

                {/* Expanded Detail Row */}
                {expandedUser === d.user_id && detailData && (
                  <TableRow>
                    <TableCell colSpan={6} className="bg-slate-50/50 p-0">
                      <div className="p-6 space-y-4">
                        {/* Personal Details */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 bg-white rounded-xl p-4 border border-slate-100">
                          <div><Label className="text-xs text-slate-400">Name</Label><p className="font-medium text-sm">{detailData.name}</p></div>
                          <div><Label className="text-xs text-slate-400">Phone</Label><p className="text-sm">{detailData.phone}</p></div>
                          <div><Label className="text-xs text-slate-400">Email</Label><p className="text-sm">{detailData.email || "N/A"}</p></div>
                          <div><Label className="text-xs text-slate-400">Address</Label><p className="text-sm">{detailData.address || "N/A"}</p></div>
                          {detailData.aadhaar_number && <div><Label className="text-xs text-slate-400">Aadhaar No.</Label><p className="text-sm font-mono">{detailData.aadhaar_number}</p></div>}
                          {detailData.license_number && <div><Label className="text-xs text-slate-400">License No.</Label><p className="text-sm font-mono">{detailData.license_number}</p></div>}
                          {detailData.vehicle_number && <div><Label className="text-xs text-slate-400">Vehicle</Label><p className="text-sm font-mono">{detailData.vehicle_number} ({detailData.vehicle_type})</p></div>}
                          {detailData.vehicle_count > 0 && <div><Label className="text-xs text-slate-400">Vehicles</Label><p className="text-sm">{detailData.vehicle_count}</p></div>}
                        </div>

                        {/* Documents Grid */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                          {(detailData.documents || []).map((doc, i) => (
                            <div key={doc.document_type || `doc-${i}`} className="bg-white rounded-xl border border-slate-100 p-4 space-y-3">
                              <div className="flex items-center justify-between">
                                <span className="font-medium text-sm text-slate-800">{doc.label}</span>
                                {statusBadge(doc.verification_status)}
                              </div>

                              {/* Preview */}
                              {doc.document_url ? (
                                <div
                                  className="relative w-full h-32 rounded-lg bg-slate-100 overflow-hidden cursor-pointer group border border-slate-200"
                                  onClick={() => setPreviewUrl(getDocUrl(doc.document_url))}
                                >
                                  {isPdf(doc.document_url) ? (
                                    <div className="flex items-center justify-center h-full">
                                      <FileText className="w-10 h-10 text-slate-400" />
                                      <span className="text-xs text-slate-500 ml-2">PDF Document</span>
                                    </div>
                                  ) : (
                                    <img src={getDocUrl(doc.document_url)} alt={doc.label} className="w-full h-full object-cover" />
                                  )}
                                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all flex items-center justify-center">
                                    <Eye className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                                  </div>
                                </div>
                              ) : (
                                <div className="w-full h-32 rounded-lg bg-slate-50 border-2 border-dashed border-slate-200 flex flex-col items-center justify-center">
                                  <AlertTriangle className="w-6 h-6 text-slate-300 mb-1" />
                                  <span className="text-xs text-slate-400">Not uploaded</span>
                                </div>
                              )}

                              {doc.verification_note && (
                                <p className="text-xs text-slate-500 bg-slate-50 p-2 rounded-lg">{doc.verification_note}</p>
                              )}

                              {/* Actions */}
                              <div className="flex gap-2">
                                <Button
                                  data-testid={`verify-${doc.document_type}-${d.user_id}`}
                                  size="sm"
                                  variant="outline"
                                  className="flex-1 text-xs h-8 rounded-lg"
                                  onClick={(e) => { e.stopPropagation(); setVerifyDialog({ open: true, userId: d.user_id, docType: doc.document_type, currentStatus: doc.verification_status }); }}
                                >
                                  <Shield className="w-3 h-3 mr-1" /> Verify
                                </Button>
                                {doc.document_url && (
                                  <Button size="sm" variant="outline" className="text-xs h-8 rounded-lg" onClick={(e) => { e.stopPropagation(); setPreviewUrl(getDocUrl(doc.document_url)); }}>
                                    <Eye className="w-3 h-3" />
                                  </Button>
                                )}
                                {doc.document_url && (
                                  <a href={getDocUrl(doc.document_url)} download onClick={(e) => e.stopPropagation()}>
                                    <Button data-testid={`download-${doc.document_type}-${d.user_id}`} size="sm" variant="outline" className="text-xs h-8 rounded-lg">
                                      <Download className="w-3 h-3" />
                                    </Button>
                                  </a>
                                )}
                                {isSuperAdmin && (
                                  <>
                                    <Button size="sm" variant="outline" className="text-xs h-8 rounded-lg" onClick={(e) => { e.stopPropagation(); setUploadDialog({ open: true, userId: d.user_id, fieldKey: doc.field_key }); }}>
                                      <Upload className="w-3 h-3" />
                                    </Button>
                                    {doc.document_url && (
                                      <Button size="sm" variant="outline" className="text-xs h-8 rounded-lg text-red-500 hover:text-red-600" onClick={(e) => { e.stopPropagation(); handleDelete(d.user_id, doc.document_type); }}>
                                        <Trash2 className="w-3 h-3" />
                                      </Button>
                                    )}
                                  </>
                                )}
                              </div>
                            </div>
                          ))}

                          {/* Journey Photos for customers */}
                          {detailData.journey_photos?.map((jp, i) => (
                            <div key={`jp-${i}`} className="bg-white rounded-xl border border-slate-100 p-4 space-y-3">
                              <span className="font-medium text-sm text-slate-800">Journey Photo</span>
                              {jp.photo_url && (
                                <div className="relative w-full h-32 rounded-lg bg-slate-100 overflow-hidden cursor-pointer group" onClick={() => setPreviewUrl(getDocUrl(jp.photo_url))}>
                                  <img src={getDocUrl(jp.photo_url)} alt="Journey" className="w-full h-full object-cover" />
                                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all flex items-center justify-center">
                                    <Eye className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </Card>

      {/* Preview Modal */}
      <Dialog open={!!previewUrl} onOpenChange={() => setPreviewUrl(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              Document Preview
            </DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-center min-h-[300px]">
            {previewUrl && (
              isPdf(previewUrl) ? (
                <iframe src={previewUrl} className="w-full h-[70vh] rounded-lg" title="PDF Preview" />
              ) : (
                <img src={previewUrl} alt="Preview" className="max-w-full max-h-[70vh] object-contain rounded-lg" />
              )
            )}
          </div>
          <div className="flex justify-end gap-2">
            {previewUrl && (
              <>
                <a href={previewUrl} download>
                  <Button data-testid="doc-download-btn" variant="outline" size="sm" className="rounded-full"><Download className="w-4 h-4 mr-1" /> Download</Button>
                </a>
                <a href={previewUrl} target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" size="sm" className="rounded-full"><Eye className="w-4 h-4 mr-1" /> Open Full Size</Button>
                </a>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Verify Dialog */}
      <Dialog open={verifyDialog.open} onOpenChange={(v) => !v && setVerifyDialog({ open: false, userId: "", docType: "", currentStatus: "" })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Verify Document</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Document: <strong>{verifyDialog.docType?.replace("_", " ")}</strong>
              <br />Current status: {statusBadge(verifyDialog.currentStatus)}
            </p>
            <div>
              <Label>Verification Note (optional)</Label>
              <Textarea
                data-testid="verify-note"
                value={verifyNote}
                onChange={(e) => setVerifyNote(e.target.value)}
                placeholder="Add a note about this verification..."
                className="mt-2 rounded-xl"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button data-testid="verify-reject-btn" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50 rounded-full" onClick={() => handleVerify("rejected")}>
              <XCircle className="w-4 h-4 mr-1" /> Reject
            </Button>
            <Button data-testid="verify-approve-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full" onClick={() => handleVerify("approved")}>
              <CheckCircle className="w-4 h-4 mr-1" /> Approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload Dialog (SuperAdmin) */}
      {isSuperAdmin && (
        <Dialog open={uploadDialog.open} onOpenChange={(v) => !v && setUploadDialog({ open: false, userId: "", fieldKey: "" })}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Upload Document</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-slate-600">Upload a new document for this user. This will replace the existing document.</p>
              <Input data-testid="doc-upload-input" type="file" accept="image/*,.pdf" onChange={handleUpload} className="rounded-xl" />
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
